import { useStore } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import nookies from 'nookies';

import { connectFromOtherDomain } from '@/redux/slices/admin.slice';
import { createWineBoxFromPush, replaceCurrentWithWineBoxFromPush, setWineBoxPush } from '@/redux/slices/box.slice';
import { hidePageProgress, setAnchor, setHeaderColor, showConfirmModal, showPageProgress } from '@/redux/slices/gui.slice';
import { selectHashedEmail, setUserSettings } from '@/redux/slices/user.slice';

import { useAppDispatch, useAppSelector } from '@/redux';
import {
  NS
} from '@/constants';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { setHeaderMobileOptions } from '@/utils/headerUtils';
import { scrollToPageTop } from '@/utils/scroll';
import { getItemListToAdd } from '@/utils/wineboxUtils';
import { LocalStorageService } from '@/services/LocalStorage.service';
import { ServicesContext } from '@/services/useServices';

import HeaderPromotionEspot from '@/components/layout/espots/HeaderPromotionEspot';
import ToastsContainer from '@/components/layout/toasts/ToastsContainer';
import CartAndBoxEventsProcess from '@/components/cart/CartAndBoxEventsProcess';
import PageProgressSpinner from '@/components/common/PageProgressSpinner';
import HeadMillesima from '@/components/layout/HeadMillesima';
import AppModals from '@/components/layout/modals/AppModals';
import Header from '@/components/layout/header/Header';
import ErrorBoundary from '@/components/ErrorBoundary';
import NavBar from '@/components/layout/navbar/NavBar';
import Footer from '@/components/layout/footer/Footer';
import buildLinkedData from '@/linkedData/index-lkd';
import JsonLd from '@/components/common/JsonLd';
import { NextComponentType, NextPageContext } from 'next/dist/shared/lib/utils';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { COOKIES } from '@/constants/cookies';
import { CUSTOM_PAGE_VIEW_EVENT, CUSTOM_PAGE_VIEW_TYPE } from '@/constants/datalayer';
import { startAssistantConversation } from '@/redux/slices/assistant.slice';
import { HEADER_TYPE } from '@/constants/header';
import { KEYS } from '@/constants/keys';
import { PAGES } from '@/constants/pages';

const AdminToasts = dynamic(() => import('@/components/layout/toasts/AdminToasts'), { ssr: false });
AdminToasts.displayName = 'AdminToasts';
const MenuMobile = dynamic(() => import('@/components/layout/navbar/mobile/MenuMobile'), { ssr: false });
MenuMobile.displayName = 'MenuMobile';
const ScrollUpButton = dynamic(() => import('@/components/layout/ScrollUpButton'), { ssr: false });
ScrollUpButton.displayName = 'ScrollUpButton';
const WineboxModal = dynamic(() => import('@/components/winebox/WineboxModal'), { ssr: false });
WineboxModal.displayName = 'WineboxModal';

type CustomAppProps = {
  Component: NextComponentType<NextPageContext, any, any>;
  pageProps: any
}

interface CustomWindow extends Window {
  createWineBox: (param: any) => void;
}

declare let window: CustomWindow;

export const CustomApp = ({ Component, pageProps }: CustomAppProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t } = useTranslation(NS.COMMON);

  const {
    appFooterVisible,
    appOverlayVisible,
    espots,
    headerColor,
    isMobileDevice,
    navBarMenu,
    showPageProgress: pageProgressVisible,
    anchor
  } = useAppSelector(({ gui }) => gui);

  const actAsCustomer = useAppSelector(({ admin }) => admin.actAsCustomer);
  const ratingCount = useAppSelector(({ root }) => root.ratingCount);
  const ratingValue = useAppSelector(({ root }) => root.ratingValue);
  const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const user = useAppSelector(({ user }) => user?.content);
  const { hashMD5, hashSha256 } = useAppSelector(selectHashedEmail);
  const wineBoxPush = useAppSelector(({ box }) => box.wineBoxPush);
  const currentBox = useAppSelector(({ box }) => box.content);

  const prevUser = useRef(loggedUser);

  const [linkedData, setLinkedData] = useState(buildLinkedData(storeConf));
  const [logo, setLogo] = useState(pageProps?.layoutOptions?.headerType === HEADER_TYPE.BLOG ? storeConf.logoBlog : storeConf.logo);
  const [backEvent, setBackEvent] = useState(false);

  const { services } = useStore();
  const { isMobileOrTablet } = useScreenSizeContext();

  const isMobileOrTabletDevice = isMobileDevice || isMobileOrTablet;
  const layoutOptions = pageProps.layoutOptions || {};
  const { datalayerService } = services;
  const { withMenu: withMenuMobile } = setHeaderMobileOptions(layoutOptions.headerType);

  const createWineBox = (param) => {
    dispatch(setWineBoxPush({ wineBoxPush: param }));
  };

  useEffect(() => {
    const handlePopState = () => {
      setBackEvent(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  if (
    typeof window !== 'undefined' &&
    !window.location.pathname.includes('primeur-checkout')
  ) {
    nookies.destroy(null, COOKIES.EXPRO.NAME);
  }

  useEffect(() => {
    const queryParams = router.query;
    if (queryParams.token && queryParams.actAsCustomer) {
      dispatch(connectFromOtherDomain({
        customerId: queryParams.actAsCustomer as string,
        storeId: storeConf.storeId,
        token: queryParams.token as string
      }));
    }

    window.createWineBox = createWineBox;

    if (queryParams.wineBoxPush) {
      dispatch(setWineBoxPush({ wineBoxPush: queryParams.wineBoxPush }));
    }
  }, []);

  useEffect(() => {
    const itemListWineBoxPush = wineBoxPush ? getItemListToAdd(wineBoxPush) : [];
    const confirmCreateWineBoxFromPush = () => {
      dispatch(replaceCurrentWithWineBoxFromPush({ itemsList: itemListWineBoxPush, t }));
      if (typeof window !== 'undefined') {
        datalayerService.pushPageName(CUSTOM_PAGE_VIEW_EVENT.CS_WB_POPIN_OPEN_CSR_URL_PV, CUSTOM_PAGE_VIEW_TYPE.MODAL_WINEBOX);
      }
    };
    const cancelCreateWineBoxFromPush = () => dispatch(setWineBoxPush({ wineBoxPush: null }));

    if (wineBoxPush && currentBox.products.length <= 0) {
      dispatch(createWineBoxFromPush({ itemsList: itemListWineBoxPush, t }));
      if (typeof window !== 'undefined') {
        datalayerService.pushPageName(CUSTOM_PAGE_VIEW_EVENT.CS_WB_POPIN_OPEN_CSR_URL_PV, CUSTOM_PAGE_VIEW_TYPE.MODAL_WINEBOX);
      }
    } else if (wineBoxPush && currentBox.products.length > 0) {
      dispatch(showConfirmModal({
        content: {
          title: t('product.box.modalPushTitle'),
          content: t('product.box.modalPushContent'),
          confirm: t('btn.delete'),
          cancel: t('btn.cancel'),
          confirmClick: confirmCreateWineBoxFromPush,
          cancelClick: cancelCreateWineBoxFromPush
        }
      }));
    }
  }, [wineBoxPush]);

  useEffect(() => {
    // We need to wait component is fully loaded to move to the anchor.
    if (anchor) {
      const handle = setTimeout(() => {
        window.location.hash = anchor;
        dispatch(setAnchor({ anchor: undefined }));
      }, 800);

      return (() => clearTimeout(handle));
    }
  }, [anchor]);

  useEffect(() => {
    setLinkedData(buildLinkedData(storeConf));
    if (typeof window !== 'undefined') {
      datalayerService.initialize(storeConf.site);
    }
    listenRouterEvents('on');

    if (loggedUser?.isAdmin) {
      const settings = LocalStorageService.getObject(KEYS.SETTINGS);
      if (!isObjectEmpty(settings)) {
        dispatch(setUserSettings({ content: settings }));
      }
    }

    return () => {
      listenRouterEvents('off');
      dispatch(hidePageProgress());
    };
  }, [storeConf, ratingValue, ratingCount]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      datalayerService.pushUser(user, loggedUser, actAsCustomer, { hashMD5, hashSha256 });
    }
  }, [user?._id]);

  const handleHidePageProgress = () => (
    dispatch(hidePageProgress())
  );

  useEffect(() => {
    const handleRouteChange = () => {
      datalayerService.pushEvent('pageview');
    };

    handleRouteChange();

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    setLogo(layoutOptions.headerType === HEADER_TYPE.BLOG ? storeConf.logoBlog : storeConf.logo);
  }, [layoutOptions]);

  useEffect(() => {
    dispatch(setHeaderColor({ actAsCustomer }));
  }, [actAsCustomer, headerColor]);

  useEffect(() => {
    if ((prevUser.current?.userId !== loggedUser?.userId) && !window?.location.href.includes('checkout')) {
      window.location.reload();
    }
  }, [loggedUser]);

  // Gestion de l'ouverture de la modale chatbot avec url param.
  useEffect(() => {
  if (!router.isReady) return;
  const handleHash = () => {
    console.log('window.location', window.location);
    console.log('router.asPath', router.asPath);
    if (window.location.hash === '#assistant') {
      history.replaceState(
        null, '', window.location.pathname + window.location.search
      );
      dispatch(startAssistantConversation());
    }
  };

  const hasAssistantHash = router.asPath.includes('#assistant');
  if (hasAssistantHash) {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    dispatch(startAssistantConversation());
  }

  handleHash();
  window.addEventListener('hashchange', handleHash);

  return () => {
    window.removeEventListener('hashchange', handleHash);
  };
}, [router.isReady]);
  
  

  const listenRouterEvents = (mode: 'on' | 'off') => {
    router.events[mode]('routeChangeStart', routeChangeStart);
    router.events[mode]('routeChangeError', handleHidePageProgress);
    router.events[mode]('routeChangeComplete', handleHidePageProgress);
  };

  const routeChangeStart = (url) => {
    // No scrollToTop if on the same page
    if (typeof window !== 'undefined' && !url.includes(window?.location?.pathname)) {
      scrollToPageTop();
    }
    tryShowPageProgress(url);
  };

  const tryShowPageProgress = (url) => {
    // Disable loader when url change to "checkout/pay" with Adyen as default payment mode (to prevent double loader)
    if (!(url === `${PAGES.CHECKOUT}/report`)) {
      dispatch(showPageProgress());
    }
  };

  const onClickMenu = (level1, level2, level3) => {
    if (!level2 && level3) {
      [level2, level3] = [level3, level2];
    }
    datalayerService.pushMenuClick(level1, level2, level3);
  };

  return (
    <ErrorBoundary>
      <ServicesContext.Provider value={services}>
        <HeadMillesima storeConf={storeConf} />
        <JsonLd data={linkedData} />
        <ToastsContainer />
        {loggedUser?.isAdmin && (
          <AdminToasts />
        )}
        {layoutOptions.withPromotionHeader && espots?.promo && (
          <HeaderPromotionEspot espot={espots.promo} />
        )}
        {layoutOptions.withHeader && (
          <Header logo={logo} backgroundColor={headerColor} headerType={layoutOptions.headerType} />
        )}
        {layoutOptions.withNavBar && (
          <NavBar navBarMenu={navBarMenu} onClickMenu={onClickMenu} />
        )}
        <div className={`app-container${appFooterVisible ? '' : ' app-container-hide'}`}>
          <div className="app-component">
            <ErrorBoundary>
              <Component {...pageProps} backEvent={backEvent} resetBackEvent={() => setBackEvent(false)} />
            </ErrorBoundary>
          </div>
          <div className={`app-overlay${appOverlayVisible ? '' : '-hide'}`} />
          {layoutOptions.withFooter && (
            <Footer
              ratingValue={ratingValue}
              hideFooter={!appFooterVisible}
              footerType={layoutOptions.footerType}
            />
          )}
        </div>
        {layoutOptions.withScrollButton && (
          <ScrollUpButton />
        )}
        {currentBox?.products?.length > 0 && layoutOptions.withWineBoxModal && (
          <WineboxModal />
        )}
        <CartAndBoxEventsProcess />
        {isMobileOrTabletDevice && withMenuMobile && (
          <MenuMobile
            showPromotionHeader={layoutOptions.withPromotionHeader}
            backgroundColor={headerColor}
            promotionEspot={espots.promo}
            onClickMenu={onClickMenu}
          />
        )}
        {pageProgressVisible && (
          <PageProgressSpinner />
        )}
        <AppModals />
        <span className="version d-none">{process.env.APP_VERSION}</span>
      </ServicesContext.Provider>
    </ErrorBoundary>
  );
};

export default CustomApp;
